import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supportApi } from '../services/supportApi';

// Public reply page — NO Entra auth required.
// Validated only by the magic-link JWT.

type Stage = 'loading' | 'invalid' | 'ready' | 'sending' | 'sent' | 'error';

export function SupportReply() {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [stage, setStage] = useState<Stage>('loading');
  const [ticket, setTicket] = useState<any>(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!token || !ticketId) {
        setStage('invalid');
        setError('Lien invalide ou expiré.');
        return;
      }
      try {
        const verify = await supportApi.verifyToken(token);
        if (!verify.valid || verify.ticketId !== ticketId) {
          setStage('invalid');
          setError(verify.error || 'Token invalide.');
          return;
        }
        const data = await supportApi.getPublicTicket(token);
        setTicket(data);
        setStage('ready');
      } catch (e: any) {
        setStage('invalid');
        setError(e.message || 'Erreur lors de la validation du lien.');
      }
    })();
  }, [token, ticketId]);

  async function handleSend() {
    if (!reply.trim()) { setError('Le message ne peut pas être vide.'); return; }
    try {
      setStage('sending');
      setError(null);
      await supportApi.addReply(token, reply);
      setStage('sent');
    } catch (e: any) {
      setError(`Échec d'envoi : ${e.message}`);
      setStage('ready');
    }
  }

  // Header O365 réutilisé partout
  const Header = (
    <div style={{ background: '#0078D4', color: '#fff', padding: '20px 24px', borderRadius: 2, marginBottom: 24 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>CLARA Support</h2>
      <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>Réponse à un ticket — Alceai</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f3f2f1', padding: '32px 16px', fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif', color: '#323130' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {Header}

        {stage === 'loading' && (
          <Card><div style={{ padding: 40, textAlign: 'center', color: '#605e5c' }}>Vérification du lien...</div></Card>
        )}

        {stage === 'invalid' && (
          <Card>
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 16, color: '#d13438', fontWeight: 600, marginBottom: 8 }}>Lien invalide</div>
              <div style={{ fontSize: 13, color: '#605e5c' }}>{error}</div>
              <div style={{ fontSize: 12, color: '#a19f9d', marginTop: 16 }}>
                Si vous avez reçu ce lien dans un email récent, contactez votre administrateur CLARA.
              </div>
            </div>
          </Card>
        )}

        {(stage === 'ready' || stage === 'sending') && ticket && (
          <Card>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{ticket.ticketNumber}</div>
                  <div style={{ fontSize: 13, color: '#605e5c', marginTop: 4 }}>{ticket.summary}</div>
                </div>
                <span style={statusPill(ticket.status)}>{ticket.status}</span>
              </div>

              {ticket.lastAgentMessage && (
                <div style={{ background: '#faf9f8', borderLeft: '4px solid #0078D4', padding: '14px 18px', borderRadius: 2, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#605e5c', fontWeight: 600, letterSpacing: 0.5, marginBottom: 6 }}>
                    Message de notre équipe
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {ticket.lastAgentMessage.preview}
                  </div>
                  <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 8 }}>
                    {new Date(ticket.lastAgentMessage.createdAt).toLocaleString('fr-FR')}
                  </div>
                </div>
              )}

              {ticket.thread && ticket.thread.length > 1 && (
                <details style={{ marginBottom: 20 }}>
                  <summary style={{ fontSize: 12, color: '#0078D4', cursor: 'pointer' }}>
                    Historique complet ({ticket.thread.length} messages)
                  </summary>
                  <div style={{ marginTop: 12 }}>
                    {ticket.thread.map((m: any, i: number) => (
                      <div key={i} style={{
                        background: m.fromParty === 'agent' ? '#faf9f8' : '#f3f9fd',
                        borderLeft: `3px solid ${m.fromParty === 'agent' ? '#0078D4' : '#107c10'}`,
                        padding: '10px 14px', marginBottom: 8, borderRadius: 2,
                      }}>
                        <div style={{ fontSize: 11, color: '#605e5c', fontWeight: 600, marginBottom: 4 }}>
                          {m.fromParty === 'agent' ? 'Alceai Support' : 'Vous'} · {new Date(m.createdAt).toLocaleString('fr-FR')}
                        </div>
                        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.preview}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {ticket.canReply ? (
                <>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#605e5c', fontWeight: 600, letterSpacing: 0.5, marginBottom: 8 }}>
                    Votre réponse
                  </div>
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    rows={8}
                    placeholder="Apportez les informations demandées par l'équipe support..."
                    disabled={stage === 'sending'}
                    style={{
                      width: '100%', padding: '10px 14px', border: '1px solid #8a8886', borderRadius: 2,
                      fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
                    }}
                  />
                  {error && <div style={{ background: '#fde7e9', border: '1px solid #d13438', color: '#a4262c', padding: '10px 14px', borderRadius: 2, fontSize: 13, marginTop: 12 }}>{error}</div>}
                  <button
                    onClick={handleSend}
                    disabled={stage === 'sending'}
                    style={{
                      marginTop: 16, background: '#0078D4', color: '#fff', border: 'none',
                      padding: '10px 24px', borderRadius: 2, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>
                    {stage === 'sending' ? 'Envoi...' : 'Envoyer la réponse →'}
                  </button>
                </>
              ) : (
                <div style={{ fontSize: 13, color: '#a19f9d', fontStyle: 'italic' }}>
                  Ce ticket n'accepte plus de réponses (statut : {ticket.status}).
                </div>
              )}
            </div>
          </Card>
        )}

        {stage === 'sent' && (
          <Card>
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ background: '#dff6dd', border: '1px solid #92c353', borderRadius: 2, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ color: '#107c10', fontWeight: 600, fontSize: 16 }}>✓ Réponse envoyée</div>
                <div style={{ fontSize: 13, color: '#605e5c', marginTop: 6 }}>
                  Votre message a bien été transmis à l'équipe Alceai. Le ticket repasse en analyse et un agent vous répondra prochainement.
                </div>
              </div>
            </div>
          </Card>
        )}

        <div style={{ textAlign: 'center', fontSize: 11, color: '#a19f9d', marginTop: 24 }}>
          CLARA · Operated by Alceai · <a href="https://alceai.io" style={{ color: '#0078D4', textDecoration: 'none' }}>alceai.io</a>
        </div>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ background: '#fff', border: '1px solid #edebe9', borderRadius: 2 }}>{children}</div>;
}

function statusPill(status: string): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    new: ['#deecf9', '#0078D4'],
    in_analysis: ['#fff4ce', '#797673'],
    waiting_client: ['#fed9cc', '#d83b01'],
    resolved: ['#dff6dd', '#107c10'],
    closed: ['#edebe9', '#605e5c'],
  };
  const [bg, fg] = map[status] || ['#edebe9', '#605e5c'];
  return {
    background: bg, color: fg, padding: '4px 10px', borderRadius: 2, fontSize: 11,
    fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
  };
}
